import { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router';

import { httpGetRedirectClrEnrlmnt, httpGetVerifyClrEnrlmnt } from '../../api/REST_services_meg/verifyClear';
import Loader from '../../components/Loader';
import { MyContext } from '../../context/MyContext';
import ClearIcon from '../../assets/svg/clear-icon.svg';

const env = typeof import.meta !== 'undefined' ? import.meta.env : process.env;

export default function Dashboard() {
  const expiryMsg1 = env.VITE_EXPIRY_MSG_1 || 'Your access level is temporarily elevated to Level 3, effective until';
  const expiryMsg2 = env.VITE_EXPIRY_MSG_2 || 'You may proceed now to badge in for entry.';
  const statusMsg = env.VITE_STATUS_MSG || 'Your access level is already set to Level 3. You may proceed now to badge in for entry.';
  const navigate = useNavigate();
  const { session_token } = useParams();
  const { contextValue, setContextValue } = useContext(MyContext);

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');

  function TimestampConverter(timestamp) {
    if (timestamp) {
      const date = new Date(timestamp);
      // Use the user's local timezone
      return date.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
    }
  }

  // Function to reset user state to initial values
  const resetUserState = () => {
    setContextValue({
      status: false,
      errorMessage: '',
      expiry: '',
      userMessage: '',
    });
    sessionStorage.removeItem('session_token');
  };

  // Error handler with a single message for all 4xx and 5xx errors, including 401 Unauthorized
  const handleError = (error) => {
    const status = error?.response?.status;
    const errorMessage = error?.response?.data?.userMessage;

    if (status === 400) {
      navigate('/error', {
        state: {
          heading: '400 Bad Request',
          description: errorMessage ? errorMessage : '',
        }
      });
    } else if (status === 401) {
      navigate('/error', {
        state: {
          heading: '401 Unauthorized',
          description: errorMessage ? errorMessage : '',
        }
      });
    } else if (status === 403) {
      navigate('/error', {
        state: {
          heading: '403 Forbidden',
          description: errorMessage ? errorMessage : '',
        }
      });
    } else if (status === 404) {
      navigate('/error', {
        state: {
          heading: '404 Not Found',
          description: errorMessage ? errorMessage : '',
        }
      });
    } else if (status >= 500) {
      navigate('/error', {
        state: {
          heading: 'Server Error',
          description: errorMessage ? errorMessage : '',
        }
      });
    } else {
      navigate('/error', {
        state: {
          heading: 'An Error Occurred',
          description: errorMessage ? errorMessage : '',
        }
      });
    }
  };

  const onVerifyClearHandler = async () => {
    try {
      setLoadingMessage('Redirecting to CLEAR for verification...');
      setLoading(true);
      resetUserState();
      const response = await httpGetRedirectClrEnrlmnt();
      const clearRedirectUrl = response?.data?.endpoint;
      window.location.href = clearRedirectUrl;
    } catch (error) {
      console.error('httpGetRedirectClrEnrlmnt submission error:', error);
      handleError(error); // Handle 401 error and others
    } finally {
      setLoading(false);
    }
  };

  const getVerifySession = async (sessionToken) => {
      try {
        setLoadingMessage('Verifying your CLEAR enrollment...');
        setLoading(true);
        const response = await httpGetVerifyClrEnrlmnt(sessionToken);
        const status = response?.data?.status || false;
        const userMessage = response?.data?.details?.message || env.VITE_DEFAULT_FALLBACK_MSG || `Whoops! Unfortunately, we weren't able to verify you. Please try again. If the issue persists please reach out to your manager for further assistance.`;
        const expiry = response?.data?.details?.expiration || ''
  
        setContextValue((prevState) => ({
          ...prevState,
          status,
          errorMessage: userMessage,
          expiry,
        }));
        // return response.data;
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      if (session_token) {
        sessionStorage.setItem('session_token', session_token);
        getVerifySession(session_token);
      }
    }, [session_token]);

  return (
    <>
      {
        loading ? <Loader message={loadingMessage} /> : (
          <div className="flex flex-col justify-center items-center w-full min-h-[80vh]">
            {
              contextValue.status ? (
                <div className="bg-white/90 rounded-md shadow-md py-4 px-6 mb-4 w-full sm:w-96">
                  <button
                    className='bg-blue-950 text-white text-lg rounded-full px-6 py-3 w-full hover:bg-blue-900 disabled:bg-slate-400'
                    disabled
                  >
                    <img src={ClearIcon} alt="clear-icon" className='inline-block w-6 h-6 mr-2' /> Verify with CLEAR
                  </button>
                </div>
              ) : (
                <div className='w-full sm:w-[32rem] animate-fadeInUp'>
                  <div className="rounded-md bg-white/90 border border-gray-300 py-4 px-8 shadow-md mb-4">
                    <button
                      className='bg-blue-950 text-white font-semibold text-base rounded-full px-6 py-3 w-full hover:bg-blue-900'
                      onClick={onVerifyClearHandler}
                    >
                      <img src={ClearIcon} alt="clear-icon" className='inline-block w-6 h-6 mr-2' /> Verify Me
                    </button>
                  </div>
                </div>
              )
            }

            {
              contextValue.status ? (
                <div className="bg-white py-4 px-6 rounded-md shadow-md w-full sm:w-96">
                  <p className="font-bold text-green-600 text-center mb-2">
                    {/* {`${} successfully verified in CLEAR`} TODO */}
                  </p>
                  <p className="font-bold text-black text-center mb-2">
                    {
                      contextValue?.expiry ?
                        `${expiryMsg1} ${TimestampConverter(contextValue?.expiry)}. ${expiryMsg2}` :
                        `${statusMsg}`
                    }
                  </p>
                </div>
              ) : (!contextValue?.status && contextValue?.errorMessage) ? (
                <div className="rounded-md bg-white/90 p-6 shadow-md mb-4">
                  <p className="text-red-600 font-bold text-center text-lg mb-2">{contextValue?.errorMessage}</p>
                </div>
              ) : (
                <></>
              )
            }
          </div>
        )
      }
    </>
  )
}
